import type { Property, Quote } from "./data/entities.ts";

/**
 * What a quote was priced on, read the same way by the admin portal and the share page: a field
 * when it has one, else the quote's input of that key (docs/data-model.md#quote-inputs). A
 * preliminary quote keeps what it read from the loan (the current policy, the cover) as inputs
 * only, since a field holds confirmed values; so does a quote whose values were never confirmed.
 * No zod here, so the portal imports it without the schemas.
 */

/** The quote's value for a key: its field, else its input's value. */
export function quoteValue(quote: Quote, key: string): unknown {
  const field = (quote as Record<string, unknown>)[key];
  if (field !== undefined && key !== "inputs") return field;
  return quote.inputs?.[key]?.value;
}

const text = (value: unknown) =>
  typeof value === "string" && value.trim() !== "" ? value : undefined;
const amount = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

/** The cover the quote asked for: its fields, else the values it was priced on. */
export function quoteCover(quote: Quote): {
  dwellingCoverage?: number;
  deductible?: string;
  liabilityLimit?: number;
  effectiveDate?: string;
} {
  const dwellingCoverage = amount(quoteValue(quote, "dwellingCoverage"));
  const deductibleValue = quoteValue(quote, "deductible");
  const deductible =
    text(deductibleValue) ??
    (amount(deductibleValue) !== undefined
      ? `$${(deductibleValue as number).toLocaleString("en-US")}`
      : undefined);
  const liabilityLimit = amount(quoteValue(quote, "liabilityLimit"));
  const effectiveDate = text(quoteValue(quote, "effectiveDate"));
  return {
    ...(dwellingCoverage !== undefined && { dwellingCoverage }),
    ...(deductible !== undefined && { deductible }),
    ...(liabilityLimit !== undefined && { liabilityLimit }),
    ...(effectiveDate !== undefined && { effectiveDate }),
  };
}

export interface CurrentPolicy {
  readonly premium: number;
  readonly carrier?: string;
  readonly expires?: string;
}

/**
 * What the client pays now: a covered property's confirmed current policy (`priorPremium`,
 * `priorCarrier`, `priorPolicyExpiration`), else the one the quote was priced against, as read from
 * the loan into its inputs.
 */
export function currentPolicyOf(
  quote: Quote | undefined,
  properties: readonly Property[],
): CurrentPolicy | undefined {
  const insured = properties.find((property) => amount(property.priorPremium) !== undefined);
  if (insured) {
    return {
      premium: insured.priorPremium as number,
      ...(text(insured.priorCarrier) && { carrier: insured.priorCarrier as string }),
      ...(text(insured.priorPolicyExpiration) && {
        expires: insured.priorPolicyExpiration as string,
      }),
    };
  }
  const premium = quote && amount(quote.inputs?.priorPremium?.value);
  if (!quote || premium === undefined) return undefined;
  const carrier = text(quote.inputs?.priorCarrier?.value);
  const expires = text(quote.inputs?.priorPolicyExpiration?.value);
  return {
    premium,
    ...(carrier !== undefined && { carrier }),
    ...(expires !== undefined && { expires }),
  };
}
